/**
 * ARCA Upcoming Events Section JS
 * Handles infinite carousel rotation for desktop 3-card bento & mobile 1.5-card track,
 * real-time event status calculation (UPCOMING, HAPPENING NOW, ENDED), and touch swipe gestures.
 */

/**
 * Calculates event status relative to the current reference date.
 * @param {string|Date} dateInput - ISO string, date string (e.g. "2026-08-24", "24 AUG 2026"), or timestamp
 * @param {Date} [referenceDate] - Optional reference date (defaults to new Date())
 * @returns {'UPCOMING' | 'HAPPENING NOW' | 'ENDED'}
 */
export function getEventStatus(dateInput, referenceDate = new Date()) {
  if (!dateInput) return 'UPCOMING';

  let eventDate;
  if (dateInput instanceof Date) {
    eventDate = dateInput;
  } else {
    const parsed = Date.parse(dateInput);
    if (isNaN(parsed)) {
      // Try parsing standard DD Mon YYYY or YYYY-MM-DD
      const cleaned = String(dateInput).trim().replace(/\s+/g, ' ');
      const fallbackParsed = Date.parse(cleaned);
      if (isNaN(fallbackParsed)) return 'UPCOMING';
      eventDate = new Date(fallbackParsed);
    } else {
      eventDate = new Date(parsed);
    }
  }

  // Compare year, month, and day in local time
  const refYear = referenceDate.getFullYear();
  const refMonth = referenceDate.getMonth();
  const refDay = referenceDate.getDate();

  const eventYear = eventDate.getFullYear();
  const eventMonth = eventDate.getMonth();
  const eventDay = eventDate.getDate();

  const refDayTimestamp = new Date(refYear, refMonth, refDay).getTime();
  const eventDayTimestamp = new Date(eventYear, eventMonth, eventDay).getTime();

  if (eventDayTimestamp < refDayTimestamp) {
    return 'ENDED';
  } else if (eventDayTimestamp === refDayTimestamp) {
    return 'HAPPENING NOW';
  } else {
    return 'UPCOMING';
  }
}

/**
 * Computes the next or previous slide index in a circular array.
 * @param {number} currentIndex 
 * @param {number} direction - +1 for next, -1 for prev
 * @param {number} totalCount 
 * @returns {number}
 */
export function calculateNextSlideIndex(currentIndex, direction, totalCount) {
  if (totalCount <= 0) return 0;
  return (currentIndex + direction + totalCount) % totalCount;
}

/**
 * Sorts an array of event objects or DOM elements so that closest upcoming events appear first.
 * Ordering:
 * 1. Today & Future upcoming events, sorted ascending by date (closest first)
 * 2. Past events (ended), sorted descending by date (most recently ended first)
 * @template T
 * @param {Array<T>} items 
 * @param {(item: T) => (string|Date)} getDateFn 
 * @param {Date} [referenceDate]
 * @returns {Array<T>}
 */
export function sortEventsByDate(items, getDateFn, referenceDate = new Date()) {
  const refTimestamp = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  ).getTime();

  return [...items].sort((a, b) => {
    const rawDateA = getDateFn(a);
    const rawDateB = getDateFn(b);

    const timeA = rawDateA ? new Date(rawDateA).getTime() : Infinity;
    const timeB = rawDateB ? new Date(rawDateB).getTime() : Infinity;

    const isUpcomingA = timeA >= refTimestamp;
    const isUpcomingB = timeB >= refTimestamp;

    if (isUpcomingA && isUpcomingB) {
      return timeA - timeB; // ascending
    }
    if (!isUpcomingA && !isUpcomingB) {
      return timeB - timeA; // most recently ended first
    }
    return isUpcomingA ? -1 : 1; // upcoming comes before ended
  });
}

const BaseElement = typeof HTMLElement !== 'undefined' ? HTMLElement : class {};

export class ArcaUpcomingEvents extends BaseElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.cardsData = [];
    this.isAnimating = false;
  }

  connectedCallback() {
    this.viewport = this.querySelector('[data-events-viewport]');
    this.desktopGrid = this.querySelector('[data-events-desktop-grid]');
    this.mobileTrack = this.querySelector('[data-events-mobile-track]');
    this.layoutVersion = this.dataset.layoutVersion || 'bento';
    this.prevButtons = Array.from(this.querySelectorAll('[data-events-prev]'));
    this.nextButtons = Array.from(this.querySelectorAll('[data-events-next]'));
    this.sourceCards = Array.from(this.querySelectorAll('[data-event-card-source]'));

    if (this.sourceCards.length === 0) return;

    this.initCardsData();
    this.updateStatusBadges();

    if (this.layoutVersion === 'grid') return;

    this.bindEvents();
    this.render();

    // Resize observer to handle breakpoint transitions cleanly
    this.resizeObserver = new ResizeObserver(() => {
      this.render();
    });
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.unbindEvents();
    this.resizeObserver?.disconnect();
  }

  initCardsData() {
    const autoSort = this.dataset.autoSort === 'true';

    let list = this.sourceCards.map((cardEl, index) => {
      return {
        id: cardEl.dataset.blockId || `card-${index}`,
        element: cardEl,
        date: cardEl.dataset.eventDate || '',
        status: cardEl.dataset.eventStatusOverride || '',
        domHtml: cardEl.innerHTML,
        dataset: { ...cardEl.dataset },
      };
    });

    if (autoSort) {
      list = sortEventsByDate(list, (item) => item.date);
    }

    this.cardsData = list;
    this.totalCards = list.length;
  }

  updateStatusBadges() {
    const now = new Date();
    this.querySelectorAll('[data-event-badge]').forEach((badge) => {
      const card = badge.closest('[data-event-date]');
      const rawDate = card?.dataset?.eventDate;
      const customStatus = card?.dataset?.eventStatusOverride;

      if (customStatus && customStatus.trim() !== '') {
        badge.textContent = customStatus;
        badge.dataset.status = customStatus.toLowerCase().replace(/\s+/g, '-');
        return;
      }

      if (rawDate) {
        const computed = getEventStatus(rawDate, now);
        badge.textContent = computed;
        badge.dataset.status = computed.toLowerCase().replace(/\s+/g, '-');
      }
    });
  }

  bindEvents() {
    this.handlePrevClick = (e) => {
      e.preventDefault();
      this.goToSlide(-1);
    };

    this.handleNextClick = (e) => {
      e.preventDefault();
      this.goToSlide(1);
    };

    this.prevButtons.forEach((btn) => btn.addEventListener('click', this.handlePrevClick));
    this.nextButtons.forEach((btn) => btn.addEventListener('click', this.handleNextClick));

    // Keyboard navigation
    this.handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        this.goToSlide(-1);
      } else if (e.key === 'ArrowRight') {
        this.goToSlide(1);
      }
    };
    this.addEventListener('keydown', this.handleKeyDown);

    // Touch Swipe handling on mobile track
    this.touchStartX = 0;
    this.touchEndX = 0;

    this.handleTouchStart = (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
    };

    this.handleTouchEnd = (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    };

    if (this.viewport) {
      this.viewport.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.viewport.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    }

    // Shopify editor integration
    document.addEventListener('shopify:block:select', (e) => {
      const blockId = e.detail?.blockId;
      if (!blockId) return;
      const foundIndex = this.cardsData.findIndex((c) => c.id === blockId);
      if (foundIndex !== -1) {
        this.currentIndex = foundIndex;
        this.render();
      }
    });
  }

  unbindEvents() {
    this.prevButtons.forEach((btn) => btn.removeEventListener('click', this.handlePrevClick));
    this.nextButtons.forEach((btn) => btn.removeEventListener('click', this.handleNextClick));
    this.removeEventListener('keydown', this.handleKeyDown);
    if (this.viewport) {
      this.viewport.removeEventListener('touchstart', this.handleTouchStart);
      this.viewport.removeEventListener('touchend', this.handleTouchEnd);
    }
  }

  handleSwipe() {
    const diff = this.touchEndX - this.touchStartX;
    if (Math.abs(diff) > 40) {
      if (diff < 0) {
        this.goToSlide(1); // Swipe left -> Next
      } else {
        this.goToSlide(-1); // Swipe right -> Prev
      }
    }
  }

  goToSlide(direction) {
    if (this.totalCards <= 1) return;
    this.currentIndex = calculateNextSlideIndex(this.currentIndex, direction, this.totalCards);
    this.render(direction);
  }

  render(direction = 0) {
    const isDesktop = window.matchMedia('(min-width: 900px)').matches;
    if (isDesktop) {
      this.renderDesktopBento(direction);
    } else {
      this.renderMobileTrack(direction);
    }
  }

  renderDesktopBento(direction) {
    if (!this.desktopGrid || this.totalCards === 0) return;

    // We populate the 3 visual slots:
    // Slot 0: Big Featured Card (Left) -> currentIndex
    // Slot 1: Top Stacked Card (Right Top) -> (currentIndex + 1) % totalCards
    // Slot 2: Bottom Stacked Card (Right Bottom) -> (currentIndex + 2) % totalCards

    const slotFeatured = this.desktopGrid.querySelector('[data-slot="featured"]');
    const slotTop = this.desktopGrid.querySelector('[data-slot="stacked-top"]');
    const slotBottom = this.desktopGrid.querySelector('[data-slot="stacked-bottom"]');

    if (!slotFeatured || !slotTop || !slotBottom) return;

    const idx0 = this.currentIndex;
    const idx1 = (this.currentIndex + 1) % this.totalCards;
    const idx2 = (this.currentIndex + 2) % this.totalCards;

    this.populateSlot(slotFeatured, this.cardsData[idx0]);
    this.populateSlot(slotTop, this.cardsData[idx1]);
    this.populateSlot(slotBottom, this.cardsData[idx2]);

    if (direction !== 0) {
      this.animateDesktopTransition();
    }
  }

  populateSlot(slotElement, cardData) {
    if (!slotElement || !cardData) return;
    slotElement.innerHTML = cardData.domHtml;
    slotElement.dataset.blockId = cardData.id;
    slotElement.dataset.eventDate = cardData.date;
    slotElement.dataset.eventStatusOverride = cardData.status;

    // Apply background image or CSS variables from source
    const sourceCard = cardData.element;
    if (sourceCard) {
      const bgImg = sourceCard.style.backgroundImage;
      if (bgImg) {
        slotElement.style.backgroundImage = bgImg;
      }
      const cssVars = ['--event-bg-image', '--event-bg-image-mobile'];
      cssVars.forEach((v) => {
        const val = sourceCard.style.getPropertyValue(v);
        if (val) slotElement.style.setProperty(v, val);
      });
    }

    this.updateStatusBadges();
  }

  animateDesktopTransition() {
    this.desktopGrid.classList.remove('is-animating');
    // Force reflow
    void this.desktopGrid.offsetWidth;
    this.desktopGrid.classList.add('is-animating');
  }

  renderMobileTrack(direction) {
    if (!this.mobileTrack || this.totalCards === 0) return;

    const mobileCards = Array.from(this.mobileTrack.querySelectorAll('[data-event-card-mobile]'));
    
    mobileCards.forEach((cardEl, idx) => {
      const actualCardIndex = (this.currentIndex + idx) % this.totalCards;
      const data = this.cardsData[actualCardIndex];
      if (data) {
        cardEl.innerHTML = data.domHtml;
        cardEl.dataset.blockId = data.id;
        cardEl.dataset.eventDate = data.date;
        cardEl.dataset.eventStatusOverride = data.status;

        const sourceCard = data.element;
        if (sourceCard) {
          const bgImg = sourceCard.style.backgroundImage;
          if (bgImg) cardEl.style.backgroundImage = bgImg;
          const cssVars = ['--event-bg-image', '--event-bg-image-mobile'];
          cssVars.forEach((v) => {
            const val = sourceCard.style.getPropertyValue(v);
            if (val) cardEl.style.setProperty(v, val);
          });
        }
      }
    });

    this.updateStatusBadges();

    if (direction !== 0) {
      this.mobileTrack.classList.remove('is-animating');
      void this.mobileTrack.offsetWidth;
      this.mobileTrack.classList.add('is-animating');
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('arca-upcoming-events')) {
  customElements.define('arca-upcoming-events', ArcaUpcomingEvents);
}
